<script setup lang="ts">
import Slider from '@vueform/slider';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import NumericInput from '@/components/forms/NumericInput.vue';
import type { NumberFormat } from '@/typings/NumberFormat';
import type { NumberRange } from '@/typings/NumberRange';

const props = defineProps<{
  allowManualInputs?: boolean
  minValue: number
  minValueLabel?: string
  maxValue: number
  maxValueLabel?: string
  sliderFormat?: object
  step?: number
  inputFormat?: NumberFormat
  integer?: boolean
}>();

const modelValue = defineModel<NumberRange>('modelValue', {
  default: { min: undefined, max: undefined } as NumberRange,
});

const minValueThreshold = ref(props.minValue);
const maxValueThreshold = ref(props.maxValue);

const { t } = useI18n();

const minValue = computed({
  get() {
    return modelValue.value?.min ?? props.minValue;
  },
  set(newValue: number) {
    const currentMax = modelValue.value?.max ?? props.maxValue;
    const validatedMin = Math.max(props.minValue, Math.min(newValue, currentMax));
    
    modelValue.value = {
      min: validatedMin,
      max: currentMax,
    };
  }
});

const maxValue = computed({
  get() {
    return modelValue.value?.max ?? props.maxValue;
  },
  set(newValue: number) {
    const currentMin = modelValue.value?.min ?? props.minValue;
    const validatedMax = Math.min(props.maxValue, Math.max(newValue, currentMin));
    
    modelValue.value = {
      min: currentMin,
      max: validatedMax,
    };
  }
});

const sliderValue = computed({
  get() {
    return [modelValue.value?.min ?? props.minValue, modelValue.value?.max ?? props.maxValue];
  },
  set(value: number[]) {
    modelValue.value = { min: value[0], max: value[1] } as NumberRange;
  }
});
</script>

<template>
  <div class="numberRange">
    <Slider v-model="sliderValue"
            :step="props.step"
            :merge="1"
            :min="minValueThreshold"
            :max="maxValueThreshold"
            :format="props.sliderFormat ?? {}"
            :tooltips="true"
            show-tooltip="drag" />
    <div class="numberRange-inputs">
      <div class="numberRange-inputContainer numberRange-inputContainer_min">
        <label>{{ t('numberRange.minValueLabel') }}</label>
        <NumericInput v-model="minValue"
                      :min="minValueThreshold"
                      :max="maxValueThreshold"
                      :step="props.step"
                      :format="props.inputFormat || 'number'"
                      :integer="props.integer"
                      :readonly="!allowManualInputs" />
      </div>
      <div class="numberRange-inputContainer numberRange-inputContainer_max">
        <label>{{ t('numberRange.maxValueLabel') }}</label>
        <NumericInput v-model="maxValue"
                      :min="minValueThreshold"
                      :max="maxValueThreshold"
                      :step="props.step" 
                      :format="props.inputFormat || 'number'"
                      :integer="props.integer"
                      :readonly="!allowManualInputs" />
      </div>
    </div>
  </div>
</template>

<style src="@vueform/slider/themes/default.css"></style>
<style lang="scss">
.numberRange {
  // Variables du double slider
  --slider-bg: var(--gray-200);
  --slider-connect-bg: var(--gray-800);
  --slider-tooltip-bg: var(--gray-600);
  --slider-tooltip-border: var(--gray-800);
  --slider-tooltip-color: var(--white);
  --slider-tooltip-font-size: 1em;
  --slider-tooltip-py: 8px;
  --slider-tooltip-px: 12px;

  &-inputs {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 1.5rem;

    > * {
      flex: 0 1 20ch;
    }
  }
}
</style>
